<template>
    <div class="editShareModal_content">
        <TabsComponent
            :tabs="tabsConfig"
            default-tab="general"
            @tab-change="handleTabChange"
        >
            <!-- Tab: Informações Gerais -->
            <template #general>
                <form @submit.prevent="handleSave" class="editShareModal_form">
                    <div class="modal-section">
                        <h4>Informações Básicas</h4>

                        <div class="row">
                            <div class="grid_col_12">
                                <div class="modal-field">
                                    <sw-input
                                        v-model="formData.title"
                                        label="Título do compartilhamento *"
                                        :validation="titleValidation"
                                        placeholder="Ex: Documentos Pessoais João Silva"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="grid_col_12">
                                <div class="modal-field">
                                    <sw-input
                                        v-model="formData.description"
                                        label="Descrição"
                                        placeholder="Descreva o conteúdo deste compartilhamento"
                                        type="textarea"
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Link Atual -->
                    <div class="modal-section">
                        <h4>Link de Compartilhamento</h4>

                        <div class="row">
                            <div class="grid_col_12">
                                <div class="editShareModal_linkDisplay">
                                    <input
                                        :value="shareData?.shareUrl"
                                        readonly
                                        class="editShareModal_linkInput"
                                        :id="`edit-link-${shareData?.id}`"
                                    />
                                    <CopyButton :textToCopy="shareData?.shareUrl" />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </template>

            <!-- Tab: Documentos Disponíveis -->
            <template #documents>
                <div class="modal-section">
                    <h4>Documentos Compartilhados</h4>
                    <p class="modal-section-description">
                        Estes são os documentos que estarão visíveis na página de compartilhamento para os visitantes.
                    </p>

                    <!-- Lista de documentos -->
                    <div v-if="getShareDocuments().length > 0" class="editShareModal_documentsList">
                        <div
                            v-for="(document, index) in sortedDocuments"
                            :key="document.id"
                            class="editShareModal_documentItem"
                            :class="{
                                'editShareModal_documentItem--dragging': getDragClasses(index).dragging,
                                'editShareModal_documentItem--dragover': getDragClasses(index)['drag-over']
                            }"
                            draggable="true"
                            @dragstart="onDragStart($event, index)"
                            @dragover="onDragOver($event)"
                            @dragenter="onDragEnter($event, index)"
                            @dragleave="onDragLeave($event)"
                            @drop="onDrop($event, index)"
                            @dragend="onDragEnd"
                        >
                            <div class="editShareModal_documentDragHandle">
                                <sw-icon name="menu" />
                            </div>
                            <div class="editShareModal_documentIcon">
                                <sw-icon :name="getDocumentIcon(document.type)" />
                            </div>
                            <div class="editShareModal_documentInfo">
                                <h5 class="editShareModal_documentName">{{ document.name }}</h5>
                                <div class="editShareModal_documentMeta">
                                    <span class="editShareModal_documentType">{{ document.type }}</span>
                                    <span class="editShareModal_documentSize">{{ document.fileSize }}</span>
                                    <span class="editShareModal_documentFormat">{{ document.fileFormat }}</span>
                                </div>
                                <div v-if="document.expiryDate" class="editShareModal_documentExpiry" :class="getExpiryClass(document.expiryDate)">
                                    <sw-icon :name="getExpiryIcon(document.expiryDate)" />
                                    <span>{{ getExpiryMessage(document.expiryDate) }}</span>
                                </div>
                            </div>
                            <div class="editShareModal_documentActions">
                                <sw-button
                                    icon="eye"
                                    height="sm"
                                    hover-color="blue"
                                    circle-icon
                                    @click="previewDocument(document)"
                                />
                                <sw-button
                                    icon="cross"
                                    size="xs"
                                    hover-color="red"
                                    circle-icon
                                    @click="removeDocument(document.id)"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Estado vazio - Apenas botão -->
                    <div v-else class="editShareModal_emptyState">
                        <sw-button
                            name="Adicionar Arquivo"
                            icon="plus"
                            color="blue"
                            @click="addDocuments"
                        />
                    </div>

                    <!-- Ações para documentos -->
                    <div v-if="getShareDocuments().length > 0" class="editShareModal_documentsActions">
                        <sw-button
                            name="Adicionar Mais"
                            icon="plus"
                            color="green"
                            size="sm"
                            @click="addDocuments"
                        />
                    </div>

                    <!-- Informações adicionais -->
                    <div class="editShareModal_documentsNote">
                        <sw-icon name="info" />
                        <div>
                            <p><strong>Como funciona:</strong></p>
                            <ul>
                                <li>Os visitantes poderão visualizar e baixar estes documentos</li>
                                <li>Documentos vencidos serão exibidos com aviso visual</li>
                                <li>Arraste pelo ícone ≡ para reorganizar a ordem dos documentos</li>
                                <li>Use as configurações de segurança para controlar o acesso</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Tab: Configurações de Segurança -->
            <template #security>
                <form @submit.prevent="handleSave" class="editShareModal_form">
                    <div class="modal-section">
                        <h4>Status e Disponibilidade</h4>

                        <div class="row mb_30">
                            <div class="grid_col_4">
                                <div class="modal-field">
                                    <sw-select
                                        v-model="formData.status"
                                        label="Status da página"
                                        :options="statusOptions"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="grid_col_6">
                                <div class="modal-field">
                                    <sw-select
                                        v-model="formData.hasExpiry"
                                        label="Data de Expiração"
                                        :options="expiryOptions"
                                        placeholder="Selecione uma opção"
                                    />
                                </div>
                            </div>

                            <div class="grid_col_6" v-if="formData.hasExpiry === 'yes'">
                                <div class="modal-field">
                                    <sw-input
                                        v-model="formData.expiryDate"
                                        label="Data de Expiração"
                                        :date-view="true"
                                        placeholder="Selecione uma data"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Controle de Acesso</h4>

                        <div class="row">
                            <div class="grid_col_12">
                                <div class="modal-field">
                                    <label class="editShareModal_fieldLabel">Tipo de Acesso</label>
                                    <div class="row">
                                        <div class="grid_col_6">
                                            <div
                                                class="editShareModal_accessType"
                                                :class="{ 'editShareModal_accessType--active': formData.accessType === 'public' }"
                                                @click="setAccessType('public')"
                                            >
                                                <div class="editShareModal_accessTypeIcon">
                                                    <sw-icon name="earth" />
                                                </div>
                                                <div class="editShareModal_accessTypeContent">
                                                    <h5>Acesso Público</h5>
                                                    <p>Qualquer pessoa com o link</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="grid_col_6">
                                            <div
                                                class="editShareModal_accessType"
                                                :class="{ 'editShareModal_accessType--active': formData.accessType === 'protected' }"
                                                @click="setAccessType('protected')"
                                            >
                                                <div class="editShareModal_accessTypeIcon">
                                                    <sw-icon name="key" />
                                                </div>
                                                <div class="editShareModal_accessTypeContent">
                                                    <h5>Acesso Protegido</h5>
                                                    <p>Requer login e senha para visualizar</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Campos condicionais para acesso protegido -->
                        <div v-if="formData.accessType === 'protected'" class="editShareModal_protectedFields">
                            <!-- Detalhes das credenciais existentes -->
                            <div v-if="shareData?.accessEmail" class="row">
                                <div class="grid_col_12">
                                    <div class="editShareModal_credentialsDisplay">
                                        <h5>Credenciais Atuais</h5>
                                        <div class="editShareModal_credentialItem">
                                            <strong>E-mail:</strong> {{ shareData.accessEmail }}
                                        </div>
                                        <div class="editShareModal_credentialItem">
                                            <strong>Senha:</strong> ••••••••
                                        </div>
                                        <div class="editShareModal_passwordWarning">
                                            <sw-icon name="alert-triangle" />
                                            <p><strong>Importante:</strong> A senha é criptografada com hash de mão única. Se esqueceu a senha, só é possível criar uma nova ou alterar a existente.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Botão para alterar credenciais -->
                            <div v-if="!showCredentialsForm" class="row">
                                <div class="grid_col_12">
                                    <div class="editShareModal_changeCredentialsButton">
                                        <sw-button
                                            name="Alterar Credenciais"
                                            hover-color="blue"
                                            icon="key"
                                            size="sm"
                                            @click="toggleCredentialsForm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Formulário para alterar credenciais -->
                            <div v-if="showCredentialsForm" class="row">
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="formData.accessEmail"
                                            label="E-mail de acesso *"
                                            type="email"
                                            placeholder="Digite o e-mail de acesso"
                                        />
                                    </div>
                                </div>

                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="formData.accessPassword"
                                            label="Nova senha de acesso *"
                                            type="password"
                                            placeholder="Digite a nova senha"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Botões de ação para formulário de credenciais -->
                            <div v-if="showCredentialsForm" class="row">
                                <div class="grid_col_12">
                                    <div class="editShareModal_credentialsActions">
                                        <sw-button
                                            name="Cancelar"
                                            color="gray"
                                            size="sm"
                                            @click="cancelCredentialsForm"
                                        />
                                        <sw-button
                                            name="Confirmar Alteração"
                                            color="blue"
                                            size="sm"
                                            @click="confirmCredentialsChange"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="grid_col_12">
                                    <div class="editShareModal_securityNote">
                                        <sw-icon name="info" />
                                        <p>As credenciais serão necessárias para acessar este compartilhamento. Certifique-se de compartilhar essas informações com segurança.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </template>

            <!-- Tab: Estatísticas -->
            <template #analytics>
                <div class="modal-section">
                    <h4>Métricas de Uso</h4>

                    <div class="row">
                        <div class="grid_col_4">
                            <div class="editShareModal_stat">
                                <div class="editShareModal_statIcon">
                                    <sw-icon name="eye" />
                                </div>
                                <div class="editShareModal_statContent">
                                    <span class="editShareModal_statNumber">{{ shareData?.stats?.views || 0 }}</span>
                                    <span class="editShareModal_statLabel">Visualizações</span>
                                </div>
                            </div>
                        </div>

                        <div class="grid_col_4">
                            <div class="editShareModal_stat">
                                <div class="editShareModal_statIcon">
                                    <sw-icon name="download" />
                                </div>
                                <div class="editShareModal_statContent">
                                    <span class="editShareModal_statNumber">{{ shareData?.stats?.downloads || 0 }}</span>
                                    <span class="editShareModal_statLabel">Downloads</span>
                                </div>
                            </div>
                        </div>

                        <div class="grid_col_4">
                            <div class="editShareModal_stat">
                                <div class="editShareModal_statIcon">
                                    <sw-icon name="calendar" />
                                </div>
                                <div class="editShareModal_statContent">
                                    <span class="editShareModal_statNumber">{{ formatDate(shareData?.createdAt) }}</span>
                                    <span class="editShareModal_statLabel">Criado em</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="grid_col_12">
                            <div class="editShareModal_analyticsNote">
                                <sw-icon name="info" />
                                <p>As estatísticas são atualizadas em tempo real e mostram o engajamento dos usuários com seu compartilhamento.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </TabsComponent>

        <!-- Actions fixos no rodapé -->
        <div class="row">
            <div class="grid_col_12">
                <div class="editShareModal_actions">
                    <sw-button
                        name="Salvar Alterações"
                        color="blue"
                        type="button"
                        :disabled="!isFormValid || saving"
                        @click="handleSave"
                    >
                        <template v-if="saving">Salvando...</template>
                        <template v-else>Salvar Alterações</template>
                    </sw-button>
                    <sw-button
                        name="Cancelar"
                        color="gray"
                        type="button"
                        @click="closeModal"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import moment from 'moment';
import { notify } from 'sysweb-vue-components/src/global';
import TabsComponent from '@Component/tabs/TabsComponent';
import CopyButton from '@Component/CopyButton.vue';
import { mockShareDocuments } from '@/data/mockData';
import { useDragAndDrop } from '@/assets/js/dragdrop/useDragAndDrop';

const props = defineProps({
    shareData: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['close', 'save']);

// Estado do formulário
const saving = ref(false);
const showCredentialsForm = ref(false);
const formData = ref({
    title: '',
    description: '',
    status: 'active',
    accessType: 'public', // 'public' ou 'protected'
    accessEmail: '',
    accessPassword: '',
    hasExpiry: 'no', // 'yes' ou 'no'
    expiryDate: ''
});

// Buscar documentos da página de compartilhamento
const getShareDocuments = () => {
    // Retorna documentos mockados baseados no ID da página
    const shareId = props.shareData?.id;
    return mockShareDocuments[shareId] || [];
};

// Computed para contagem de documentos
const documentsCount = computed(() => {
    const shareId = props.shareData?.id;
    return mockShareDocuments[shareId]?.length || 0;
});

// Usar composable de drag and drop
const {
    draggingIndex,
    dragOverIndex,
    itemOrder,
    onDragStart,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    onDragEnd,
    getSortedItems,
    getDragClasses,
    setCustomOrder
} = useDragAndDrop({
    onReorder: (newOrder, details) => {
        console.log('Documentos reordenados:', newOrder, details);
    },
    messages: {
        success: 'Ordem dos documentos alterada! Lembre-se de salvar as alterações.',
        error: 'Erro ao alterar ordem dos documentos'
    }
});

// Computed para documentos ordenados
const sortedDocuments = computed(() => {
    const docs = getShareDocuments();
    return getSortedItems(docs);
});

// Configuração das tabs
const tabsConfig = computed(() => [
    {
        id: 'general',
        label: 'Geral',
        icon: 'cog'
    },
    {
        id: 'documents',
        label: 'Documentos',
        icon: 'folder',
        badge: documentsCount.value
    },
    {
        id: 'security',
        label: 'Segurança',
        icon: 'shield'
    },
    {
        id: 'analytics',
        label: 'Analytics',
        icon: 'pie-chart',
        badge: props.shareData?.stats?.views || 0
    }
]);

// Opções de status
const statusOptions = computed(() => [
    { value: 'active', label: 'Ativo', selected: formData.value.status === 'active' },
    { value: 'inactive', label: 'Inativo', selected: formData.value.status === 'inactive' },
    { value: 'expired', label: 'Expirado', selected: formData.value.status === 'expired' }
]);

// Opções de expiração
const expiryOptions = computed(() => [
    { value: 'no', label: 'Sem expiração', selected: formData.value.hasExpiry === 'no' },
    { value: 'yes', label: 'Definir data de expiração', selected: formData.value.hasExpiry === 'yes' }
]);

// Validações
const titleValidation = computed(() => ({
    isValid: formData.value.title.trim().length >= 3,
    error: {
        message: formData.value.title.trim().length > 0 && formData.value.title.trim().length < 3
            ? 'Título deve ter pelo menos 3 caracteres'
            : ''
    }
}));

const isFormValid = computed(() => {
    return titleValidation.value.isValid;
});

// Methods
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('YYYY-MM-DD');
};

const loadShareData = (shareData) => {
    if (!shareData) return;

    formData.value = {
        title: shareData.title || '',
        description: shareData.description || '',
        status: shareData.status || 'active',
        accessType: shareData.accessType || 'public',
        accessEmail: shareData.accessEmail || '',
        accessPassword: '', // Não carregamos a senha por segurança
        hasExpiry: shareData.expiryDate ? 'yes' : 'no',
        expiryDate: shareData.expiryDate ? formatDateForInput(shareData.expiryDate) : ''
    };

    // Carregar ordem dos documentos se existir
    if (shareData.documentsOrder && Array.isArray(shareData.documentsOrder)) {
        setCustomOrder(shareData.documentsOrder);
        console.log('Ordem dos documentos carregada:', shareData.documentsOrder);
    }
};

// Watchers
watch(() => props.shareData, (newData) => {
    if (newData) {
        loadShareData(newData);
    }
}, { immediate: true });

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(moment(dateString).toDate());
};


const handleSave = async () => {
    if (!isFormValid.value) return;

    try {
        saving.value = true;

        // Obter ordem atual dos documentos
        const currentDocuments = getShareDocuments();
        const orderedDocuments = getSortedItems(currentDocuments);

        const updatedData = {
            ...props.shareData,
            ...formData.value,
            // Converter data de volta para ISO se foi fornecida e se hasExpiry for 'yes'
            expiryDate: formData.value.hasExpiry === 'yes' && formData.value.expiryDate
                ? moment(formData.value.expiryDate).toISOString()
                : null,
            // Incluir ordem dos documentos
            documentsOrder: itemOrder.value,
            documents: orderedDocuments
        };

        console.log('Salvando dados com ordem dos documentos:', {
            documentsOrder: itemOrder.value,
            orderedDocuments: orderedDocuments
        });

        // Simular delay de API
        await new Promise(resolve => setTimeout(resolve, 1000));

        emit('save', updatedData);

    } catch (error) {
        console.error('Erro ao salvar compartilhamento:', error);
        notify('Erro ao salvar alterações. Tente novamente.', 'error');
    } finally {
        saving.value = false;
    }
};

const closeModal = () => {
    emit('close');
};

const handleTabChange = (tabId) => {
    console.log('Tab mudou para:', tabId);
};


const setAccessType = (type) => {
    formData.value.accessType = type;
    console.log('Tipo de acesso alterado para:', type);

    // Limpar campos de credenciais se mudou para público
    if (type === 'public') {
        formData.value.accessEmail = '';
        formData.value.accessPassword = '';
        showCredentialsForm.value = false;
    }
};

const toggleCredentialsForm = () => {
    showCredentialsForm.value = true;
};

const cancelCredentialsForm = () => {
    showCredentialsForm.value = false;
    // Restaura valores originais
    if (props.shareData?.accessEmail) {
        formData.value.accessEmail = props.shareData.accessEmail;
    }
    formData.value.accessPassword = '';
};

const confirmCredentialsChange = () => {
    // Validação básica
    if (!formData.value.accessEmail || !formData.value.accessPassword) {
        notify('Por favor, preencha todos os campos obrigatórios.', 'error');
        return;
    }

    showCredentialsForm.value = false;
    notify('Credenciais alteradas com sucesso! Lembre-se de salvar as alterações.', 'success');
};

// Funções para documentos
const getDocumentIcon = (type) => {
    const iconMap = {
        'CNH': 'credit-card',
        'RG': 'user',
        'CPF': 'user',
        'Título de Eleitor': 'users',
        'Passaporte': 'file-text',
        'Carteira de Vacinação': 'file-text',
        'Comprovante de Residência': 'home',
        'CRLV': 'credit-card',
        'CNPJ': 'office',
        'Alvará': 'file-text',
        'Certidão': 'file-text',
        'Diploma': 'file-text',
        'Certificado': 'file-text',
        'Contrato de Aluguel': 'home'
    };
    return iconMap[type] || 'file-text';
};

const getExpiryClass = (expiryDate) => {
    if (!expiryDate) return '';

    const today = moment();
    const expiry = moment(expiryDate);
    const diffDays = expiry.diff(today, 'days');

    if (diffDays <= 0) return 'editShareModal_expired';
    if (diffDays <= 30) return 'editShareModal_expiring';
    return 'editShareModal_valid';
};

const getExpiryIcon = (expiryDate) => {
    if (!expiryDate) return 'calendar';

    const today = moment();
    const expiry = moment(expiryDate);
    const diffDays = expiry.diff(today, 'days');

    if (diffDays <= 0) return 'cross';
    if (diffDays <= 30) return 'notification';
    return 'checkmark';
};

const getExpiryMessage = (expiryDate) => {
    if (!expiryDate) return '';

    const today = moment();
    const expiry = moment(expiryDate);
    const diffDays = expiry.diff(today, 'days');

    if (diffDays <= 0) {
        const overdueDays = Math.abs(diffDays);
        return overdueDays === 0 ? 'Vence hoje' : `Vencido há ${overdueDays} dia${overdueDays > 1 ? 's' : ''}`;
    }
    if (diffDays <= 30) {
        return `Vence em ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    }
    return `Válido por ${diffDays} dias`;
};

const previewDocument = (document) => {
    console.log('Visualizar documento:', document);
    notify('Funcionalidade de preview será implementada em breve!', 'info');
};

const removeDocument = (documentId) => {
    console.log('Remover documento:', documentId);
    if (confirm('Tem certeza que deseja remover este documento do compartilhamento?')) {
        // TODO: Implementar remoção do documento
        notify('Documento removido do compartilhamento!', 'success');
    }
};

const addDocuments = () => {
    console.log('Adicionar documentos ao compartilhamento');
    notify('Modal de seleção de documentos será implementado em breve!', 'info');
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.editShareModal_content {
    padding: 0;
}

.editShareModal_form {
    display: flex;
    flex-direction: column;
    gap: 2em;
}

/* Estilos das seções já estão definidos globalmente em modalSections.scss */

.editShareModal_fieldLabel {
    display: block;
    font-size: 0.875em;
    font-weight: 600;
    color: var(--textSecondary, #6b7280);
    margin-bottom: 0.5em;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.editShareModal_fieldHelp {
    display: block;
    font-size: 0.75em;
    color: var(--textSecondary, #6b7280);
    margin-top: 0.5em;
    font-style: italic;
}


/* Removido - usando sysweb grid system */

.editShareModal_stat {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 1em;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
    border: 1px solid rgba(59, 130, 246, 0.1);
    border-radius: 0.75em;
}

.editShareModal_statIcon {
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.5em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    flex-shrink: 0;
}

.editShareModal_statContent {
    display: flex;
    flex-direction: column;
    gap: 0.25em;
}

.editShareModal_statNumber {
    font-size: 1.125em;
    font-weight: 700;
    color: var(--textPrimary, #1f2937);
}

.editShareModal_statLabel {
    font-size: 0.75em;
    color: var(--textSecondary, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.025em;
    font-weight: 500;
}

.editShareModal_linkDisplay {
    display: flex;
    gap: 0.75em;
    align-items: center;
    flex-wrap: wrap;
}

.editShareModal_linkInput {
    flex: 1;
    padding: 0.75em 1em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    background: var(--backgroundInsideBox, #f8fafc);
    color: var(--textPrimary);
    font-size: 0.875em;
    font-family: monospace;

    &:focus {
        outline: none;
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
}

.editShareModal_actions {
    display: flex;
    justify-content: flex-end;
    gap: 1em;
    padding-top: 1.5em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    margin-top: 1em;
}

.editShareModal_analyticsNote {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    padding: 1.25em;
    margin-top: 2em;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
    border: 1px solid rgba(59, 130, 246, 0.1);
    border-radius: 0.75em;
    color: var(--textSecondary, #64748b);

    .sw-icon {
        color: var(--colorBlue, #3b82f6);
        font-size: 1.25em;
        flex-shrink: 0;
        margin-top: 0.125em;
    }

    p {
        margin: 0;
        font-size: 0.875em;
        line-height: 1.6;
    }
}

/* Removido - usando sysweb grid system */

.editShareModal_accessType {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    padding: 1.25em;
    border: 2px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    cursor: pointer;
    transition: all 0.3s ease;
    background: var(--backgroundCard, #ffffff);

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    }

    &--active {
        border-color: var(--colorBlue, #3b82f6);
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);

        .editShareModal_accessTypeIcon {
            background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
            color: white;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        h5 {
            color: var(--colorBlue, #3b82f6);
        }
    }
}

.editShareModal_accessTypeIcon {
    width: 3em;
    height: 3em;
    border-radius: 0.75em;
    background: rgba(107, 114, 128, 0.1);
    color: var(--textSecondary, #6b7280);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25em;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.editShareModal_accessTypeContent {
    flex: 1;

    h5 {
        margin: 0 0 0.5em 0;
        font-size: 1em;
        font-weight: 700;
        color: var(--textPrimary, #1f2937);
        transition: color 0.3s ease;
    }

    p {
        margin: 0;
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
        line-height: 1.4;
    }
}

.editShareModal_protectedFields {
    margin-top: 2em;
    padding-top: 2em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    animation: fadeInUp 0.4s ease-out;
}

.editShareModal_securityNote {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    padding: 1.25em;
    margin-top: 1.5em;
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(251, 191, 36, 0.03));
    border: 1px solid rgba(245, 158, 11, 0.1);
    border-radius: 0.75em;
    color: var(--textSecondary, #64748b);

    .sw-icon {
        color: var(--colorOrange, #f59e0b);
        font-size: 1.25em;
        flex-shrink: 0;
        margin-top: 0.125em;
    }

    p {
        margin: 0;
        font-size: 0.875em;
        line-height: 1.6;
    }
}

.editShareModal_credentialsDisplay {
    padding: 1.5em;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
    border: 1px solid rgba(59, 130, 246, 0.1);
    border-radius: 0.75em;
    margin-bottom: 1.5em;

    h5 {
        margin: 0 0 1em 0;
        font-size: 1em;
        font-weight: 600;
        color: var(--textPrimary, #1f2937);
        text-transform: uppercase;
        letter-spacing: 0.025em;
    }
}

.editShareModal_credentialItem {
    display: flex;
    gap: 0.5em;
    margin-bottom: 0.75em;
    font-size: 0.875em;

    strong {
        color: var(--textSecondary, #64748b);
        min-width: 4em;
    }

    &:last-of-type {
        margin-bottom: 1.5em;
    }
}

.editShareModal_passwordWarning {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    padding: 1em;
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(251, 191, 36, 0.05));
    border: 1px solid rgba(245, 158, 11, 0.15);
    border-radius: 0.5em;

    .sw-icon {
        color: var(--colorOrange, #f59e0b);
        font-size: 1em;
        flex-shrink: 0;
        margin-top: 0.125em;
    }

    p {
        margin: 0;
        font-size: 0.8em;
        line-height: 1.5;
        color: var(--textSecondary, #64748b);

        strong {
            color: var(--colorOrange, #f59e0b);
        }
    }
}

.editShareModal_changeCredentialsButton {
    display: flex;
    justify-content: center;
}

.editShareModal_credentialsActions {
    display: flex;
    justify-content: center;
    gap: 1em;
    margin-top: 1em;
    padding-top: 1em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(0.5em);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}


/* Responsividade */
@include screen_medias(s, m) {
    .editShareModal_linkDisplay {
        flex-direction: column;
        gap: 0.5em;
    }

    .editShareModal_actions {
        flex-direction: column;
    }

    .editShareModal_accessType {
        padding: 1em;
    }

    .editShareModal_accessTypeIcon {
        width: 2.5em;
        height: 2.5em;
        font-size: 1em;
    }

    .editShareModal_documentItem {
        flex-direction: column;
        align-items: flex-start;
        gap: 1em;
    }

    .editShareModal_documentActions {
        width: 100%;
        justify-content: space-between;
    }
}

/* =======================
   DOCUMENTOS STYLES
   ======================= */
.editShareModal_documentsList {
    display: flex;
    flex-direction: column;
    gap: 1em;
    margin-bottom: 2em;
}

.editShareModal_documentItem {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 1.25em;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.6));
    border: 1px solid rgba(226, 232, 240, 0.8);
    border-radius: 0.875em;
    transition: all 0.3s ease;
    cursor: grab;

    &:hover {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.8));
        border-color: rgba(59, 130, 246, 0.3);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:active {
        cursor: grabbing;
    }

    &--dragging {
        opacity: 0.3;
        transform: scale(0.95);
        cursor: grabbing;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 197, 253, 0.05));
        border: 2px dashed rgba(59, 130, 246, 0.4);
    }

    &--dragover {
        border: 2px solid var(--colorBlue, #3b82f6);
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 197, 253, 0.08));
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(59, 130, 246, 0.2);
    }
}

.editShareModal_documentDragHandle {
    width: 1.5em;
    height: 2em;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--textSecondary, #9ca3af);
    cursor: grab;
    border-radius: 0.25em;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        color: var(--colorBlue, #3b82f6);
        background: rgba(59, 130, 246, 0.1);
    }

    &:active {
        cursor: grabbing;
    }

    .sw-icon {
        font-size: 1em;
        transform: rotate(90deg);
    }
}

.editShareModal_documentIcon {
    width: 3em;
    height: 3em;
    border-radius: 0.75em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorPurple, #8b5cf6));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125em;
    flex-shrink: 0;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
}

.editShareModal_documentInfo {
    flex: 1;
    min-width: 0;
}

.editShareModal_documentName {
    font-size: 1em;
    font-weight: 600;
    color: var(--textPrimary, #1f2937);
    margin: 0 0 0.5em 0;
    line-height: 1.3;
}

.editShareModal_documentMeta {
    display: flex;
    align-items: center;
    gap: 0.75em;
    margin-bottom: 0.5em;
    flex-wrap: wrap;
}

.editShareModal_documentType {
    font-size: 0.75em;
    font-weight: 600;
    color: var(--colorBlue, #3b82f6);
    background: rgba(59, 130, 246, 0.1);
    padding: 0.25em 0.5em;
    border-radius: 0.375em;
    border: 1px solid rgba(59, 130, 246, 0.2);
}

.editShareModal_documentSize,
.editShareModal_documentFormat {
    font-size: 0.75em;
    font-weight: 500;
    color: var(--textSecondary, #64748b);
    background: rgba(107, 114, 128, 0.08);
    padding: 0.25em 0.5em;
    border-radius: 0.375em;
    border: 1px solid rgba(107, 114, 128, 0.2);
}

.editShareModal_documentExpiry {
    display: inline-flex;
    align-items: center;
    gap: 0.375em;
    font-size: 0.75em;
    font-weight: 600;
    padding: 0.375em 0.5em;
    border-radius: 0.375em;
    width: fit-content;

    &.editShareModal_expired {
        background: rgba(239, 68, 68, 0.1);
        color: var(--colorRed, #dc2626);
        border: 1px solid rgba(239, 68, 68, 0.2);
    }

    &.editShareModal_expiring {
        background: rgba(245, 158, 11, 0.1);
        color: var(--colorOrange, #d97706);
        border: 1px solid rgba(245, 158, 11, 0.2);
    }

    &.editShareModal_valid {
        background: rgba(16, 185, 129, 0.1);
        color: var(--colorGreen, #059669);
        border: 1px solid rgba(16, 185, 129, 0.2);
    }

    .sw-icon {
        font-size: 0.875em;
    }
}

.editShareModal_documentActions {
    display: flex;
    gap: 0.5em;
}

.editShareModal_emptyState {
    display: flex;
    justify-content: center;
    padding: 2em 0;
    margin-bottom: 2em;
}

.editShareModal_documentsActions {
    display: flex;
    gap: 1em;
    justify-content: center;
    margin-bottom: 2em;
    padding-top: 1.5em;
    border-top: 1px solid rgba(226, 232, 240, 0.6);
}

.editShareModal_documentsNote {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    padding: 1.25em;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
    border: 1px solid rgba(59, 130, 246, 0.1);
    border-radius: 0.75em;
    color: var(--textSecondary, #64748b);

    p {
        margin: 0 0 0.75em 0;
        font-size: 0.875em;
        line-height: 1.6;

        strong {
            color: var(--textPrimary, #1f2937);
        }
    }

    ul {
        margin: 0;
        padding-left: 1.25em;
        font-size: 0.875em;
        line-height: 1.6;

        li {
            margin-bottom: 0.375em;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}
</style>